import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import type { ProviderConnection } from '@ocktra/shared-types';

export interface NotificationSettings {
    priceAlerts: boolean;
    newsUpdates: boolean;
    tradingSignals: boolean;
    marketOpen: boolean;
}

export interface SettingsState {
    country: string;
    currency: string;
    timezone: string;
    broker: string;
    displayName: string;
    notifications: NotificationSettings;
    // Connected providers with their credentials
    dataFeedProviders: ProviderConnection[];
    tradingProviders: ProviderConnection[];
    // Last selected trading provider for order placement
    preferredTradingProvider: string | null;
}

const initialState: SettingsState = {
    country: 'IN',
    currency: 'INR',
    timezone: 'America/New_York',
    broker: '',
    displayName: '',
    notifications: {
        priceAlerts: true,
        newsUpdates: false,
        tradingSignals: true,
        marketOpen: true,
    },
    dataFeedProviders: [],
    tradingProviders: [],
    preferredTradingProvider: null,
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        setCountry: (
            state,
            action: PayloadAction<{ country: string; currency: string; timezone: string }>
        ) => {
            state.country = action.payload.country;
            state.currency = action.payload.currency;
            state.timezone = action.payload.timezone;
        },
        setBroker: (state, action: PayloadAction<string>) => {
            state.broker = action.payload;
        },
        setDisplayName: (state, action: PayloadAction<string>) => {
            state.displayName = action.payload;
        },
        updateNotification: (
            state,
            action: PayloadAction<{ key: keyof NotificationSettings; value: boolean }>
        ) => {
            state.notifications[action.payload.key] = action.payload.value;
        },
        addDataFeedProvider: (state, action: PayloadAction<ProviderConnection>) => {
            state.dataFeedProviders.push(action.payload);
        },
        updateDataFeedProvider: (state, action: PayloadAction<ProviderConnection>) => {
            const index = state.dataFeedProviders.findIndex(
                (p) => p.providerName === action.payload.providerName
            );
            if (index !== -1) {
                state.dataFeedProviders[index] = action.payload;
            }
        },
        removeDataFeedProvider: (state, action: PayloadAction<string>) => {
            state.dataFeedProviders = state.dataFeedProviders.filter((p) => p.id !== action.payload);
        },
        addTradingProvider: (state, action: PayloadAction<ProviderConnection>) => {
            state.tradingProviders.push(action.payload);
        },
        updateTradingProvider: (state, action: PayloadAction<ProviderConnection>) => {
            const index = state.tradingProviders.findIndex(
                (p) => p.providerName === action.payload.providerName
            );
            if (index !== -1) {
                state.tradingProviders[index] = action.payload;
            }
        },
        removeTradingProvider: (state, action: PayloadAction<string>) => {
            state.tradingProviders = state.tradingProviders.filter((p) => p.id !== action.payload);
        },
        setPreferredTradingProvider: (state, action: PayloadAction<string>) => {
            state.preferredTradingProvider = action.payload;
        },
        saveSettings: (state, action: PayloadAction<Partial<SettingsState>>) => {
            return { ...state, ...action.payload };
        },
        resetSettings: () => initialState,
    },
});

export const {
    setCountry,
    setBroker,
    setDisplayName,
    updateNotification,
    addDataFeedProvider,
    updateDataFeedProvider,
    removeDataFeedProvider,
    addTradingProvider,
    updateTradingProvider,
    removeTradingProvider,
    setPreferredTradingProvider,
    saveSettings,
    resetSettings,
} = settingsSlice.actions;

export const selectSettings = (state: RootState) => state.settings;
export const selectCountry = (state: RootState) => state.settings.country;
export const selectCurrency = (state: RootState) => state.settings.currency;
export const selectTimezone = (state: RootState) => state.settings.timezone;
export const selectBroker = (state: RootState) => state.settings.broker;
export const selectDisplayName = (state: RootState) => state.settings.displayName;
export const selectDataFeedProviders = (state: RootState) => state.settings.dataFeedProviders
export const selectTradingProviders = (state: RootState) => state.settings.tradingProviders;
export const selectPreferredTradingProvider = (state: RootState) => state.settings.preferredTradingProvider;
export const selectNotifications = (state: RootState) => state.settings.notifications;

export default settingsSlice.reducer;
